/**
 * CC-1b — auditServerChannel.
 *
 * # Why this exists (ТЗ §14 audit, fullstack-gap-closure §3 CC-1b)
 *
 * The in-memory audit ring (`pushAuditEvent`) was never persisted: the CC-1d
 * admin audit console reads the Laravel `audit_events` table, but nothing wrote
 * it, so the console rendered only the 3 seeded rows. This module is the
 * missing WRITE transport — it projects each emitted audit-ring entry into the
 * single-source `AuditRecord` wire shape (`@dxf-ai-editor/contracts`), batches
 * them, and POSTs to `POST /api/v1/audit` with backpressure + retry so audit
 * events are never silently lost.
 *
 * # Why a SEPARATE channel from telemetry
 *
 * CC-1a's `telemetryServerChannel` flushes the SAME ring into `telemetry_events`
 * (aggregate analytics: counts by kind/status/command_type). `audit_events` is
 * the richer PER-ACTION audit log the console filters + detail-panels. The two
 * are distinct tables + contracts, so the audit flush is its own channel with
 * its own wire shape. It does NOT re-observe the store, though: the CC-1a
 * subscriber already owns the tricky STAGE-while-open / commit-vs-abort ring
 * logic, so it tees each committed event into `projectAuditEventToAuditRecord`
 * + this channel via its `auditSink` hook (see
 * `installAuditTelemetryTransport`). Both channels therefore observe an
 * IDENTICAL, abort-filtered event set.
 *
 * The delivery machinery (batch / retry / in-flight identity pinning /
 * backpressure / auth-drop / validation-drop) is the shared
 * `BatchedServerChannel`; this module supplies only the audit validators + wire
 * batch build + the ring→record projection.
 */

import {
  AUDIT_ENTITY_IDS_MAX,
  AUDIT_SCHEMA_VERSION,
  AUDIT_VARCHAR_MAX,
  auditBatchSchema,
  auditRecordSchema,
  type AuditEvent,
  type AuditBatch,
  type AuditPersistStatus,
  type AuditRecord,
  type TelemetryTone,
} from "@dxf-ai-editor/contracts";

import {
  BatchedServerChannel,
  createFetchBatchTransport,
  generateClientEventUid,
  type BatchedServerChannelTuning,
  type BatchFlushResult,
} from "./batchedServerChannel";

export const AUDIT_INGEST_PATH = "/api/v1/audit";

export type AuditFlushResult = BatchFlushResult;

/** Pluggable delivery sink. Production wires `createFetchAuditTransport`. */
export type AuditTransport = (batch: AuditBatch) => Promise<AuditFlushResult>;

export interface AuditServerChannelOptions extends BatchedServerChannelTuning {
  transport: AuditTransport;
}

/**
 * Batched, retrying, backpressured audit-persist channel — a specialization of
 * the shared `BatchedServerChannel` (it INHERITS the public
 * `enqueue`/`flush`/`idle`/`size`/`stats`/`dispose` surface + the whole
 * reliability loop; only the audit validators + wire batch build are supplied).
 */
export class AuditServerChannel extends BatchedServerChannel<
  AuditRecord,
  AuditBatch
> {
  constructor(options: AuditServerChannelOptions) {
    super({
      // Spread the caller tuning (transport + batchSize/maxChunkSize/… ) so the
      // knob list is never re-enumerated here.
      ...options,
      validateEvent: (record) => {
        const parsed = auditRecordSchema.safeParse(record);
        return parsed.success ? (parsed.data as AuditRecord) : null;
      },
      buildBatch: (events) => ({
        schemaVersion: AUDIT_SCHEMA_VERSION,
        events,
      }),
      validateBatch: (batch) => {
        const validated = auditBatchSchema.safeParse(batch);
        if (validated.success) return validated.data as AuditBatch;
        options.onError?.(validated.error);
        return null;
      },
      // Wire contract cap — `auditBatchSchema.events` is `.max(500)`.
      hardChunkCap: 500,
    });
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Audit-ring entry → AuditRecord projection
// ──────────────────────────────────────────────────────────────────────────

/**
 * Map the audit-ring outcome `tone` onto the persisted `audit_events.status`
 * domain. `error` → `error`, `warning` → `warning`, everything else
 * (`info`/`success`) → `success` (a successful outcome). Mirrors the telemetry
 * `toneToStatus`, but into the audit status enum (`success`, not `ok`).
 */
export function toneToAuditStatus(tone: TelemetryTone): AuditPersistStatus {
  if (tone === "error") return "error";
  if (tone === "warning") return "warning";
  return "success";
}

/** Read a string scalar off the typed ring params, or `undefined`. */
function paramString(
  params: AuditEvent["params"],
  key: string,
): string | undefined {
  const value = params?.[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * DROP every UNPAIRED UTF-16 surrogate in `value`, leaving well-formed surrogate
 * PAIRS intact. A high surrogate (0xD800–0xDBFF) is unpaired unless the NEXT code
 * unit is a low surrogate; a low surrogate (0xDC00–0xDFFF) is unpaired unless the
 * PREVIOUS unit was a high surrogate. Dropping (rather than substituting) keeps
 * the result strictly shorter-or-equal, so it never grows past a downstream
 * length bound — and matches the truncation-boundary contract (a `slice` that
 * orphans a trailing high surrogate drops it).
 *
 * Why: `JSON.stringify` happily emits a bare `\uD83D` for a lone surrogate, but
 * PHP/Laravel's `json_decode` rejects the whole request body as malformed UTF-16
 * BEFORE the ingest controller can return a terminal 422 — a 400-class failure
 * the batched channel treats as transient and retries forever, head-of-line-
 * blocking every later audit event. Malformed UTF-16 can enter from ANY value
 * (e.g. a pasted / truncated failed-command message), not just at a truncation
 * boundary, so the repair must scan the whole string.
 *
 * Lazily allocates: the common (well-formed) path returns the SAME reference with
 * no copy; a new string is built only from the first offending unit onward.
 */
export function repairLoneSurrogates(value: string): string {
  let out: string | null = null;
  for (let i = 0; i < value.length; i += 1) {
    const unit = value.charCodeAt(i);
    if (unit >= 0xd800 && unit <= 0xdbff) {
      // High surrogate — valid only if immediately followed by a low surrogate.
      const next = i + 1 < value.length ? value.charCodeAt(i + 1) : 0;
      if (next >= 0xdc00 && next <= 0xdfff) {
        // Well-formed pair: keep BOTH units, skip the partner index.
        if (out !== null) out += value[i]! + value[i + 1]!;
        i += 1;
        continue;
      }
      // Lone high surrogate — drop it (start the lazy copy at this index).
      if (out === null) out = value.slice(0, i);
      continue;
    }
    if (unit >= 0xdc00 && unit <= 0xdfff) {
      // Lone low surrogate (a valid pair is consumed above, so reaching here
      // means this low half is orphaned) — drop it.
      if (out === null) out = value.slice(0, i);
      continue;
    }
    if (out !== null) out += value[i];
  }
  return out ?? value;
}

/**
 * Bounded, schema-valid stand-in for a `command` that repairs to EMPTY — see
 * `clampVarchar`. Well under `AUDIT_VARCHAR_MAX` and contains no surrogates (so
 * `repairLoneSurrogates` is a no-op on it and it can never itself need repair).
 */
const AUDIT_UNREPRESENTABLE_COMMAND_FALLBACK =
  "(command text unrepresentable: contained only malformed UTF-16)";

/**
 * Clamp a value to the `audit_events` VARCHAR width so a legitimately long
 * `command` SUMMARY (e.g. a verbose failed-command / parse-error message) yields
 * a schema-valid, insert-safe record — TRUNCATED to fit — instead of failing the
 * contract's `.max()` and being dropped at `validateEvent` (or overflowing the
 * column into a retried 500). Bounded to the SAME `AUDIT_VARCHAR_MAX` the
 * contract enforces (one source of truth).
 *
 * Repairs malformed UTF-16 — a bare high/low surrogate makes PHP/Laravel
 * `json_decode` reject the whole body as a retried 400 that head-of-line-blocks
 * the queue, and such an orphan can appear anywhere (pasted malformed UTF-16 in a
 * failed-command message), not only at a truncation boundary. A short summary is
 * repaired whole; a summary LONGER than the column is clamped to its first
 * `AUDIT_VARCHAR_MAX` code units FIRST and only that prefix is repaired — a huge
 * pasted summary must never drag the repair scan (and its lazy re-concatenation)
 * across a multi-KB tail that the clamp immediately discards. One repair pass over
 * the prefix also drops a high surrogate orphaned by cutting through a valid pair
 * at the boundary (`AUDIT_VARCHAR_MAX` is a UTF-16 code-UNIT count); `repair` only
 * ever shortens, so the result stays within the column width.
 *
 * `repairLoneSurrogates` can shorten an ENTIRELY-malformed prefix all the way to
 * `""` (e.g. a pasted/programmatic summary that is nothing but unpaired
 * surrogates). `auditRecordSchema.command` is `.min(1)`, so an empty command
 * fails `validateEvent` and the WHOLE record — alias/status/entityIds/timing
 * evidence included — is silently rejected, never queued, never delivered:
 * exactly the loss this repair exists to prevent, just moved one layer down. A
 * non-empty prefix that repairs to empty therefore falls back to a bounded,
 * surrogate-free placeholder so the record stays schema-valid and deliverable.
 */
function clampVarchar(value: string): string {
  // Only the first `AUDIT_VARCHAR_MAX` code units can ever reach the wire, so
  // slice FIRST (a no-op when already under the width) and repair ONLY that
  // prefix. This bounds the work (and any allocation) to O(AUDIT_VARCHAR_MAX)
  // instead of scanning + re-concatenating the entire discarded tail of a very
  // large summary. A single repair pass suffices: it drops every lone surrogate
  // in the slice, including a high surrogate orphaned by the cut at the
  // `AUDIT_VARCHAR_MAX` boundary.
  const prefix =
    value.length <= AUDIT_VARCHAR_MAX ? value : value.slice(0, AUDIT_VARCHAR_MAX);
  const repaired = repairLoneSurrogates(prefix);
  if (repaired.length === 0 && prefix.length > 0) {
    return AUDIT_UNREPRESENTABLE_COMMAND_FALLBACK;
  }
  return repaired;
}

/**
 * Resolve the AFFECTED entity ids for the audit record. Prefers the id list
 * stamped VERBATIM on the ring entry at emit time (`affectedEntityIdsForCommand`
 * → `AuditEvent.entityIds`), which covers the multi-entity commands (selection /
 * CHPROP / properties-apply / matchprop / geometry-modify) whose typed `params`
 * carry only an aggregate `entityCount`. Falls back to a single `params.entityId`
 * for a pre-slice ring snapshot persisted before the `entityIds` field existed.
 *
 * The list is TRUNCATED to `AUDIT_ENTITY_IDS_MAX` (the same cap the contract
 * enforces, one source of truth) so a huge selection (SELECT-ALL + CHPROP over a
 * large drawing) yields a schema-valid, request-size-safe record — carrying the
 * first `AUDIT_ENTITY_IDS_MAX` affected ids — instead of being dropped whole at
 * `validateEvent` or inflating the batch body past the ingest size limit.
 */
function resolveAffectedEntityIds(audit: AuditEvent): string[] {
  if (audit.entityIds && audit.entityIds.length > 0) {
    return audit.entityIds.length > AUDIT_ENTITY_IDS_MAX
      ? audit.entityIds.slice(0, AUDIT_ENTITY_IDS_MAX)
      : [...audit.entityIds];
  }
  const single = paramString(audit.params, "entityId");
  return single ? [single] : [];
}

export interface AuditRecordProjectionOverrides {
  /** Resolved outcome tone; falls back to the ring entry's own tone, then `info`. */
  tone?: TelemetryTone;
  /** Emit time (ms since epoch). */
  timestampMs?: number;
  /**
   * Injectable per-record dedupe key. Production omits it (a fresh
   * collision-resistant uid is minted per projection); tests pass a fixed value
   * to assert retry-stability deterministically.
   */
  clientEventUid?: string;
  /**
   * Active space id from the store snapshot — the fallback `spaceContext` when
   * the ring entry's typed params carry no per-command `spaceId`.
   */
  activeSpaceId?: string;
}

/**
 * Project an in-memory audit-ring entry into the persisted `AuditRecord` wire
 * shape. Every field is copied from the REAL ring entry — never inferred:
 *   - `command`      ← the human-readable `summary` (the console's Command
 *                      column + command filter),
 *   - `alias`        ← the machine `commandType` (the alias arm the command
 *                      filter also matches),
 *   - `commandSource`← the ring `source`,
 *   - `spaceContext` ← the per-command `params.spaceId` when present, else the
 *                      snapshot's active space, else `model`,
 *   - `entityIds`    ← the AFFECTED-id list stamped on the ring entry at emit
 *                      time (`AuditEvent.entityIds`, covering multi-entity
 *                      selection / CHPROP / apply / modify ops), falling back to
 *                      a single `params.entityId` for a pre-slice snapshot,
 *   - `status`       ← the tone→status projection.
 *
 * `hotkey` / `beforeSummary` / `afterSummary` are `null`: the lean audit ring is
 * a post-hoc summary that does not track a bound hotkey or a distinct
 * before/after state (enriching the ring to carry those is a separate CC-1c
 * emitter-side concern, not this transport). Nullable columns, never a
 * placeholder value.
 */
export function projectAuditEventToAuditRecord(
  audit: AuditEvent,
  overrides: AuditRecordProjectionOverrides = {},
): AuditRecord {
  const tone = overrides.tone ?? audit.tone ?? "info";
  const spaceContext =
    paramString(audit.params, "spaceId") ??
    (overrides.activeSpaceId && overrides.activeSpaceId.length > 0
      ? overrides.activeSpaceId
      : undefined) ??
    "model";
  return {
    schemaVersion: AUDIT_SCHEMA_VERSION,
    clientEventUid: overrides.clientEventUid ?? generateClientEventUid(),
    // `command` is a varchar-backed column — clamp a long summary to fit so the
    // record stays schema-valid + insert-safe (never dropped / never a 500).
    command: clampVarchar(audit.summary),
    alias: audit.commandType,
    hotkey: null,
    commandSource: audit.source,
    spaceContext,
    entityIds: resolveAffectedEntityIds(audit),
    beforeSummary: null,
    afterSummary: null,
    status: toneToAuditStatus(tone),
    occurredAtMs: overrides.timestampMs ?? Date.now(),
  };
}

// ──────────────────────────────────────────────────────────────────────────
// Production transport
// ──────────────────────────────────────────────────────────────────────────

/**
 * No-op delivery sink for the "no backend audit endpoint configured" case.
 * Reports `ok:true` so the channel drains the batch (no retry storm, no
 * unbounded queue growth) — silently dropping is correct when there is nowhere
 * to POST.
 */
const NO_OP_AUDIT_TRANSPORT: AuditTransport = () => Promise.resolve({ ok: true });

export interface FetchAuditTransportOptions {
  /**
   * Backend origin. `undefined` means NO audit endpoint is configured — only
   * then is a no-op transport returned. An EMPTY STRING is the normalized
   * same-origin root and MUST POST (mirrors the telemetry transport contract).
   */
  baseUrl: string | undefined;
  fetchImpl?: typeof fetch;
  path?: string;
  /** Unload-flush hook forwarded to `createFetchBatchTransport` (keepalive POST). */
  keepalive?: () => boolean;
}

/**
 * Default delivery sink: a credentialed JSON POST to the Laravel audit ingest
 * endpoint (session cookie + XSRF header), via the shared
 * `createFetchBatchTransport`. Only `undefined` `baseUrl` yields the no-op
 * transport; `''` is the valid same-origin root and POSTs to `/api/v1/audit`.
 */
export function createFetchAuditTransport(
  options: FetchAuditTransportOptions,
): AuditTransport {
  const { baseUrl } = options;
  if (baseUrl === undefined) return NO_OP_AUDIT_TRANSPORT;

  return createFetchBatchTransport<AuditBatch>({
    baseUrl,
    path: options.path ?? AUDIT_INGEST_PATH,
    fetchImpl: options.fetchImpl,
    keepalive: options.keepalive,
  });
}
